import { PrintRubricTable } from "@vericus/cadmus-common";
import { GridIcon } from "@vericus/cadmus-icons";
import { Modal, Text, VisuallyHidden } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { LoadingQuill } from "@/components/loading-quill";
import { EmptyRubric } from "@/features/materials/components/marker-rubric/empty-rubric";
import {
  MarkerRubricFragment,
  useMarkerRubricByIdQuery,
} from "@/generated/graphql";

import * as styles from "./marker-rubric-modal.css";

interface Props {
  markerRubricId: string;
}

/**
 * Modal showing a read-only MarkerRubric to a student. Used in contexts where
 * navigating away from the page (e.g. a live Socratic attempt) is not possible.
 */
export const MarkerRubricModal = create<Props>((props) => {
  const { markerRubricId } = props;

  const modal = useModal();

  const { data, loading, error } = useMarkerRubricByIdQuery({
    variables: { markerRubricId },
    skip: !modal.visible,
  });

  const markerRubric = data?.markerRubricById ?? null;

  const title = markerRubric
    ? `Marking Rubric ${buildRubricPathSuffix(markerRubric)}`
    : "Marking Rubric";

  return (
    <Modal.Root
      open={modal.visible}
      onOpenChange={(open) => {
        if (!open) modal.hide();
      }}
    >
      <Modal.Content width="85vw" onCloseButtonClick={() => modal.hide()}>
        <VisuallyHidden.Root>
          <Modal.Title>{title}</Modal.Title>
        </VisuallyHidden.Root>
        <div className={styles.body}>
          {loading ? (
            <div className={styles.loading}>
              <LoadingQuill />
            </div>
          ) : error || !markerRubric ? (
            <div className={styles.empty}>
              <EmptyRubric />
            </div>
          ) : (
            <>
              <div className={styles.title}>
                <div className={styles.iconWrapper}>
                  <GridIcon size={24} label="" />
                </div>
                <Text kind="headingFour">{title}</Text>
              </div>

              <PrintRubricTable
                criteria={markerRubric.criteria}
                format={markerRubric.format}
                reversedScale={markerRubric.reversedScale}
              />
            </>
          )}
        </div>
      </Modal.Content>
    </Modal.Root>
  );
});

function buildRubricPathSuffix(markerRubric: MarkerRubricFragment): string {
  const { questionShortPrompt, location } = markerRubric;
  if (questionShortPrompt !== null && questionShortPrompt !== "") {
    return location.path.split(questionShortPrompt)[1] ?? location.path;
  }
  return location.path;
}
